import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import postService from '../../services/postService';
import ImageUpload from '../common/ImageUpload';
import RichTextEditor from '../common/RichTextEditor';
import { societyService, extractSocietyResults } from '../../services/societyService';
import {
  saveFormData,
  loadFormData,
  clearFormData,
  pickPersistedFormFields,
} from '../../utils/formPersistence';
import { sanitizeHTML } from '../../utils/htmlSanitizer';
import { getSlugFromParams } from '../../utils/urlHelpers';
import LoginPromptModal from '../common/LoginPromptModal';
import SelectSocietyModal from '../common/SelectSocietyModal';
import SocietySearchableSelect from '../common/SocietySearchableSelect';

const PostCreateForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, isAuthenticated, fetchUserData } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [loadingPost, setLoadingPost] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSelectSocietyModal, setShowSelectSocietyModal] = useState(false);
  
  // Check if we're in edit mode
  const postIdentifier = getSlugFromParams(params, 'post');
  const isEditMode = !!postIdentifier;
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'news',
    society: '',
    is_public: false
  });
  const [userSociety, setUserSociety] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const formDraftRestoredRef = useRef(false);
  const societiesLoadedForUserRef = useRef(null);
  const userId = user?.id;
  const societyIdFromUrl = searchParams.get('society');

  // Load post data if in edit mode
  useEffect(() => {
    const loadPostForEdit = async () => {
      if (!isEditMode || !postIdentifier) return;
      
      try {
        setLoadingPost(true);
        const postData = await postService.getPost(postIdentifier);
        
        // Populate form with existing post data
        setFormData({
          title: postData.title || '',
          content: postData.content || '', // This will be HTML if it was formatted
          category: postData.category || 'news',
          society: (postData.society && typeof postData.society === 'object' ? postData.society.id : postData.society) || '',
          is_public: postData.is_public || false
        });
        
        // Set existing images
        if (postData.images && postData.images.length > 0) {
          setExistingImages(postData.images);
        }
      } catch (error) {
        console.error('Error loading post for edit:', error);
        showError('Failed to load post data. Please try again.');
        // Navigate back on error
        navigate(-1);
      } finally {
        setLoadingPost(false);
      }
    };

    if (isEditMode && isAuthenticated) {
      loadPostForEdit();
    }
  }, [isEditMode, postIdentifier, isAuthenticated, navigate, showError]);

  // Restore saved draft once (create mode only)
  useEffect(() => {
    if (isEditMode || formDraftRestoredRef.current) {
      return;
    }
    formDraftRestoredRef.current = true;

    const savedFields = pickPersistedFormFields(loadFormData('post'));
    if (savedFields) {
      setFormData((prev) => ({
        ...prev,
        ...savedFields,
      }));
      setSelectedImages([]);
      showSuccess('Your previous form data has been restored!');
    }
  }, [isEditMode, showSuccess]);

  // Load societies once per user (create and edit)
  useEffect(() => {
    if (!userId || societiesLoadedForUserRef.current === userId) {
      return undefined;
    }

    let cancelled = false;

    const loadSocieties = async () => {
      try {
        setLoadingSocieties(true);

        const userIsAdmin =
          user.is_staff ||
          user.is_superuser ||
          ['admin', 'moderator'].includes(user.role);
        if (!cancelled) {
          setIsAdmin(userIsAdmin);
        }

        if (userIsAdmin) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          if (cancelled) {
            return;
          }
          setSocieties(allSocieties);
          if (societyIdFromUrl) {
            const sid = parseInt(societyIdFromUrl, 10);
            const selectedSociety = allSocieties.find((s) => s.id === sid);
            if (selectedSociety) {
              setFormData((prev) => ({
                ...prev,
                society: prev.society || selectedSociety.id,
              }));
            }
          }
        } else if (user.society) {
          if (cancelled) {
            return;
          }
          setUserSociety(user.society);
          setSocieties([user.society]);
          if (!isEditMode) {
            setFormData((prev) => ({
              ...prev,
              society: prev.society || user.society.id,
            }));
          }
        } else {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          if (cancelled) {
            return;
          }
          setSocieties(allSocieties);
          if (societyIdFromUrl) {
            const sid = parseInt(societyIdFromUrl, 10);
            const selectedSociety = allSocieties.find((s) => s.id === sid);
            if (selectedSociety) {
              setFormData((prev) => ({
                ...prev,
                society: prev.society || selectedSociety.id,
              }));
            }
          }
        }

        societiesLoadedForUserRef.current = userId;
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading societies:', error);
          showError('Failed to load societies. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoadingSocieties(false);
        }
      }
    };

    loadSocieties();

    return () => {
      cancelled = true;
    };
  }, [userId, societyIdFromUrl, showError, isEditMode]);

  // Save form data whenever it changes
  useEffect(() => {
    if (formData.title || formData.content) {
      saveFormData('post', formData);
    }
  }, [formData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = (content) => {
    // Sanitize HTML content before storing
    const sanitizedContent = sanitizeHTML(content);
    setFormData(prev => ({
      ...prev,
      content: sanitizedContent
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    setLoading(true);

    try {
      const postData = {
        ...formData,
        images: selectedImages
      };
      
      if (isEditMode) {
        // Update existing post
        await postService.updatePost(postIdentifier, postData);
        showSuccess('Post updated successfully!');
        
        // Navigate to the post detail page
        const isSocietyContext = location.pathname.includes('/societies/');
        if (isSocietyContext) {
          const pathParts = location.pathname.split('/');
          const societySlugIndex = pathParts.indexOf('societies') + 1;
          const societySlug = pathParts[societySlugIndex];
          navigate(`/societies/${societySlug}/posts/${postIdentifier}`);
        } else {
          navigate(`/posts/${postIdentifier}`);
        }
      } else {
        // Create new post
        await postService.createPost(postData);
        
        // Clear saved form data on successful submission
        clearFormData('post');
        
        showSuccess('Post created successfully! It will be reviewed by admin before being published.');
        
        // Check if we're in a society context and navigate back appropriately
        const isSocietyContext = location.pathname.includes('/societies/');
        if (isSocietyContext) {
          // Extract society slug from the current path
          const pathParts = location.pathname.split('/');
          const societySlugIndex = pathParts.indexOf('societies') + 1;
          const societySlug = pathParts[societySlugIndex];
          navigate(`/societies/${societySlug}/posts`);
        } else {
          navigate('/posts');
        }
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} post:`, error);
      showError(`Failed to ${isEditMode ? 'update' : 'create'} post. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    navigate('/?auth=login');
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const categoryOptions = [
    { value: 'news', label: 'News / Current Events' },
    { value: 'technology', label: 'Technology / Gadgets' },
    { value: 'business', label: 'Business / Economy' },
    { value: 'health', label: 'Health / Wellness' },
    { value: 'lifestyle', label: 'Lifestyle / Culture' },
    { value: 'entertainment', label: 'Entertainment / Media' },
    { value: 'sports', label: 'Sports' },
    { value: 'education', label: 'Education / Career' },
    { value: 'opinion', label: 'Opinion / Editorials' },
    { value: 'qa', label: 'Ask & Answer / Q&A' },
    { value: 'travel', label: 'Points / Places' },
    { value: 'visa', label: 'Visa / Immigration' },
    { value: 'recipes', label: 'Recipes / Food' },
    { value: 'announcements', label: 'Announcements / Updates' },
    { value: 'local', label: 'Local / Regional Topics' },
    { value: 'deals', label: 'Deals / Offers' },
    { value: 'other', label: 'Other' },
  ];

  // Show loading state when loading post for edit
  if (isEditMode && loadingPost) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading post...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEditMode ? 'Edit Post' : 'Create New Post'}
        </h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Society: admins pick any; members with profile society are locked; others pick from list */}
          {isAdmin ? (
            <SocietySearchableSelect
              societies={societies}
              value={formData.society}
              onChange={handleChange}
              loading={loadingSocieties}
            />
          ) : userSociety ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Society
              </label>
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                {`${userSociety.name} - ${userSociety.city}`}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                You can only create posts for your own society.
              </p>
            </div>
          ) : (
            <div>
              <SocietySearchableSelect
                id="society-member-pick"
                societies={societies}
                value={formData.society}
                onChange={handleChange}
                loading={loadingSocieties}
                helperText="Choose which society this post is for. It will be reviewed before publication."
              />
              <button
                type="button"
                onClick={() => setShowSelectSocietyModal(true)}
                className="mt-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
              >
                Save a default society on my profile
              </button>
            </div>
          )}

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter post title"
            />
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <RichTextEditor
              value={formData.content}
              onChange={handleContentChange}
              placeholder="Write your post content here..."
              rows={6}
              disabled={loading}
            />
            {/* Hidden input for form validation */}
            <input
              type="hidden"
              name="content"
              value={formData.content}
              required
            />
          </div>

          {/* Images */}
          <ImageUpload
            selectedImages={selectedImages}
            onImagesChange={setSelectedImages}
            maxImages={5}
            disabled={loading}
            onError={(msg) => { if (msg) showError(msg); }}
          />

          {/* Public Visibility */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_public"
              name="is_public"
              checked={formData.is_public}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
              Make this post public (visible to everyone)
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/posts')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!isAdmin && !formData.society)}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Post' : 'Create Post')}
            </button>
          </div>
        </form>

        {/* Info Message */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Your post will be reviewed by an admin before being published and visible to the public.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={handleCloseLoginModal}
        onLogin={handleLogin}
        title="Login Required to Create Post"
        message="You need to be logged in to create a post. Your form data has been saved and will be restored after login."
      />

      {/* Select society modal when user has no society */}
      <SelectSocietyModal
        isOpen={showSelectSocietyModal}
        onClose={() => setShowSelectSocietyModal(false)}
        onSuccess={async () => {
          await fetchUserData();
        }}
      />
    </div>
  );
};

export default PostCreateForm;
